import React from 'react';

// Status icons and colors
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'approved':
      return (
        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center" data-testid="status-approved">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case 'rejected':
      return (
        <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center" data-testid="status-rejected">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    case 'resubmit':
      return (
        <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center" data-testid="status-resubmit">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      );
    case 'pending':
    default:
      return (
        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center" data-testid="status-pending">
          <div className="w-3 h-3 rounded-full bg-slate-400" />
        </div>
      );
  }
};

// Connector line between steps
const Connector = ({ isActive, isSequential }) => (
  <div className={`flex-1 h-1 mx-2 rounded ${isSequential ? '' : 'hidden md:block'} ${
    isActive ? 'bg-green-400' : 'bg-slate-200'
  }`} />
);

// Individual step in the tracker
const TrackerStep = ({ name, status, comments, isLast, isSequential, isCurrent, stepNumber }) => (
  <div className={`flex flex-col items-center ${isSequential ? 'flex-1' : 'w-full md:flex-1'}`}>
    <div className="relative">
      <StatusIcon status={status} />
      {isCurrent && status === 'pending' && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
        </span>
      )}
    </div>
    <div className="mt-2 text-center">
      <div className={`text-xs font-semibold ${isCurrent ? 'text-indigo-600' : 'text-slate-700'}`}>
        {isSequential ? `Step ${stepNumber}` : `Reviewer ${stepNumber}`}
      </div>
      <div className="text-sm font-medium text-slate-900 max-w-[100px] truncate" title={name}>
        {name}
      </div>
      <div className={`text-xs mt-0.5 px-2 py-0.5 rounded-full inline-block ${
        status === 'approved' ? 'bg-green-100 text-green-700' :
        status === 'rejected' ? 'bg-red-100 text-red-700' :
        status === 'resubmit' ? 'bg-amber-100 text-amber-700' :
        'bg-slate-100 text-slate-600'
      }`}>
        {status || 'pending'}
      </div>
    </div>
    {comments && (
      <div className="mt-1 text-xs text-slate-500 max-w-[120px] text-center truncate" title={comments}>
        "{comments}"
      </div>
    )}
  </div>
);

export const SubmittalProgressTracker = ({ submittal }) => {
  // Build reviewers list
  const reviewers = [
    { 
      name: submittal.assigned_to_name || 'Reviewer 1', 
      status: submittal.approval_1 || 'pending',
      comments: submittal.approval_1_comments
    }
  ];
  
  if (submittal.assigned_to_2) {
    reviewers.push({ 
      name: submittal.assigned_to_2_name || 'Reviewer 2', 
      status: submittal.approval_2 || 'pending',
      comments: submittal.approval_2_comments
    });
  }
  
  if (submittal.assigned_to_3) {
    reviewers.push({ 
      name: submittal.assigned_to_3_name || 'Reviewer 3', 
      status: submittal.approval_3 || 'pending',
      comments: submittal.approval_3_comments
    });
  }

  const isSequential = submittal.workflow_type === 'sequential';
  const currentReviewer = submittal.current_reviewer || 1;
  const isPendingResubmission = submittal.status === 'pending_resubmission';
  
  // Calculate overall progress
  const totalReviewers = reviewers.length;
  const approvedCount = reviewers.filter(r => r.status === 'approved').length;
  const progressPercent = (approvedCount / totalReviewers) * 100;

  // Determine overall status
  const hasRejected = reviewers.some(r => r.status === 'rejected');
  const hasResubmit = reviewers.some(r => r.status === 'resubmit');
  const allApproved = reviewers.every(r => r.status === 'approved');

  // Response history
  const responseHistory = submittal.response_history || [];
  const attachments = submittal.attachments || [];

  return (
    <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200" data-testid="submittal-progress-tracker">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-semibold ${
            isSequential ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'
          }`}>
            {isSequential ? 'Sequential Workflow' : 'Simultaneous Workflow'}
          </span>
          <span className="text-xs text-slate-500">
            {totalReviewers} reviewer{totalReviewers > 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {allApproved && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700">
              All Approved
            </span>
          )}
          {hasRejected && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700">
              Rejected
            </span>
          )}
          {hasResubmit && !hasRejected && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700">
              Resubmit Required
            </span>
          )}
          {isPendingResubmission && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-700">
              Awaiting Initiator Response
            </span>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
          <span>Approval Progress</span>
          <span>{approvedCount}/{totalReviewers} approved</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              hasRejected ? 'bg-red-500' : 
              hasResubmit ? 'bg-amber-500' : 
              allApproved ? 'bg-green-500' : 'bg-indigo-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Sequential Workflow - Timeline View */}
      {isSequential ? (
        <div className="flex items-start justify-between">
          {reviewers.map((reviewer, index) => (
            <React.Fragment key={index}>
              <TrackerStep
                name={reviewer.name}
                status={reviewer.status}
                comments={reviewer.comments}
                isLast={index === reviewers.length - 1}
                isSequential={true}
                isCurrent={currentReviewer === index + 1}
                stepNumber={index + 1}
              />
              {index < reviewers.length - 1 && (
                <div className="flex items-center pt-4 flex-1 px-2">
                  <Connector 
                    isActive={reviewer.status === 'approved'} 
                    isSequential={true}
                  />
                  <svg className={`w-4 h-4 ${
                    reviewer.status === 'approved' ? 'text-green-400' : 'text-slate-300'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        /* Simultaneous Workflow - Parallel View */
        <div className="flex flex-col md:flex-row items-stretch justify-around gap-4">
          {reviewers.map((reviewer, index) => (
            <div key={index} className="flex-1 bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
              <TrackerStep
                name={reviewer.name}
                status={reviewer.status}
                comments={reviewer.comments}
                isLast={index === reviewers.length - 1}
                isSequential={false}
                isCurrent={false}
                stepNumber={index + 1}
              />
            </div>
          ))}
        </div>
      )}

      {/* Raised By Info */}
      <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>Raised by: <strong className="text-slate-700">{submittal.assigned_by_name || 'Unknown'}</strong></span>
        </div>
        <div className="flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>Due: <strong className="text-slate-700">{new Date(submittal.due_date).toLocaleDateString()}</strong></span>
        </div>
      </div>

      {/* Response History */}
      {responseHistory.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-200">
          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Response History ({responseHistory.length})</div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {responseHistory.slice().reverse().map((entry, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs p-2 bg-white rounded border border-slate-100">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  entry.response_type === 'resubmission' ? 'bg-blue-500' :
                  entry.status === 'approved' ? 'bg-green-500' :
                  entry.status === 'rejected' ? 'bg-red-500' :
                  entry.status === 'resubmit' ? 'bg-amber-500' : 'bg-slate-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{entry.user_name}</span>
                    <span className="text-slate-400">{new Date(entry.created_at).toLocaleString()}</span>
                  </div>
                  {entry.comments && <p className="text-slate-600 mt-0.5">{entry.comments}</p>}
                  {entry.file_url && (
                    <a href={entry.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 mt-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {entry.file_name || 'Attachment'}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-200">
          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Attachments ({attachments.length})</div>
          <div className="flex flex-wrap gap-2">
            {attachments.map((att, idx) => (
              <a 
                key={idx}
                href={att.file_url} 
                target="_blank" 
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"
              >
                <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {att.file_name}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
